import { FC } from 'react'

import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { useTiktokCampaignDetails } from 'src/Redux/campaignTiktokDetailsApi/useTiktokCampaignDetails'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { useTiktokCampaignVideos } from 'src/Redux/campaignVideosApi/useTiktokCampaignVideos'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useReels } from 'src/Hooks/useReels'
import { useCampaignPayment } from 'src/Hooks/useCampaignPayment'

import { TikTokDetailsBreadcrumbs } from './Components/TikTokDetailsBreadcrumbs'
import { SummarySection } from './Components/SummarySection'
import { ResultSection } from './Components/ResultSection'
import { useTikTokCampaignDetailsPage } from './useTikTokCampaignDetailsPage'
import { useTypeformForEndedTikTokCampaigns } from './Components/Survey/useTypeformForEndedTikTokCampaigns'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 24px;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    padding-bottom: 56px;
    gap: 40px;
  }
`

const Content = styled.div`
  display: flex;
  max-width: 1120px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    flex-direction: column;
  }
`

export const TiktokCampaignDetailsPage: FC = () => {
  useTypeformForEndedTikTokCampaigns()
  const navigate = useNavigate()
  useTikTokCampaignDetailsPage()
  const { campaignDetails, isLoading } = useTiktokCampaignDetails()
  const { campaignVideos, isLoading: isVideosLoading } =
    useTiktokCampaignVideos()

  const { isDetailsForReelsCampaign } = useReels()

  const genresList = campaignDetails?.genres?.map((genre) => genre?.name || '')

  const onStartCampaignClick = (): void => {
    navigate(`${ArtistRoutesEnum.TiktokSchedule}/${campaignDetails?.id}`)
  }

  const campaignStatus =
    campaignDetails?.status as unknown as CampaignStatusEnum

  const payment = useCampaignPayment(
    CampaignPlatformTypeEnum.TIKTOK,
    campaignDetails?.id,
  )

  const amount = campaignDetails?.amount ?? 0
  const spent = campaignDetails?.spent ?? 0

  const credits = Math.max(amount - spent, 0)

  const platform = isDetailsForReelsCampaign
    ? CampaignPlatformTypeEnum.REELS
    : CampaignPlatformTypeEnum.TIKTOK

  return (
    <PageContainer>
      <TikTokDetailsBreadcrumbs />
      <Content>
        <SummarySection
          campaignStatus={campaignStatus}
          payment={payment}
          loading={isLoading}
          spent={spent}
          trackCoverUrl={campaignDetails?.track?.coverUrl}
          trackName={campaignDetails?.track?.title}
          trackAuthorName={campaignDetails?.track?.authorName}
          startDate={campaignDetails?.startDate}
          endDate={campaignDetails?.endDate}
          genres={genresList}
          budget={campaignDetails?.amount?.toString()}
          reserved={campaignDetails?.reserved}
          hasNotCompletedEvents={campaignDetails?.hasNotCompletedEvents}
          credits={credits}
          platform={platform}
          commentType={
            !isDetailsForReelsCampaign ? campaignDetails?.commentType : null
          }
          lyricsLink={
            !isDetailsForReelsCampaign ? campaignDetails?.lyricsLink : null
          }
        />
        <ResultSection
          startDate={campaignDetails?.startDate}
          campaignStatus={campaignStatus}
          campaignId={campaignDetails?.id}
          payment={payment}
          loading={isLoading || isVideosLoading}
          viewsCount={campaignDetails?.stats?.totalViews}
          commentsCount={campaignDetails?.stats?.totalComments}
          likesCount={campaignDetails?.stats?.totalLikes}
          videosCount={campaignDetails?.stats?.totalVideos}
          videos={campaignVideos?.videos}
          onStartCampaignClick={onStartCampaignClick}
          pricingModel={campaignDetails?.pricingModel}
          expectedMaxVideos={campaignDetails?.expectedMaxVideos}
          expectedMaxViews={campaignDetails?.expectedMaxViews}
          expectedMinVideos={campaignDetails?.expectedMinVideos}
          expectedMinViews={campaignDetails?.expectedMinViews}
        />
      </Content>
    </PageContainer>
  )
}
